import React, { useState, useCallback } from 'react';
import { TProductSortingItem, TSecondarySortOption } from '../model';
import { SortOptionRow } from './SortOptionRow';

const COMPONENT_ID = 'product-sorting-table';

type ProductSortingTableProps = {
    sortOptions: TProductSortingItem[];
    onReorder: (reorderedOptions: TProductSortingItem[]) => void;
    onVisibilityChange: (primarySortId: string, sortOrder: number, isVisible: boolean) => void;
    onSecondarySortChange: (primarySortId: string, sortOrder: number, secondarySortId: string, secondarySortTitle: string) => void;
    onTitleChange?: (primarySortId: string, sortOrder: number, title: string) => void;
    onDelete?: (primarySortId: string) => void;
    secondarySortOptions: TSecondarySortOption[];
};

const headerClasses = 'tw-px-4 tw-py-3 tw-text-left tw-text-sm tw-font-semibold tw-text-ink-secondary';

export const ProductSortingTable = ({
    sortOptions,
    onReorder,
    onVisibilityChange,
    onSecondarySortChange,
    onTitleChange,
    onDelete,
    secondarySortOptions,
}: ProductSortingTableProps) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const handleDragStart = useCallback(
        (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
            setDraggedIndex(index);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', index.toString());
        },
        []
    );

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';

            if (draggedIndex !== null && draggedIndex !== index) {
                setDragOverIndex(index);
            }
        },
        [draggedIndex]
    );

    const handleDragEnd = useCallback(() => {
        if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
            const reorderedOptions = [...sortOptions];
            const [removed] = reorderedOptions.splice(draggedIndex, 1);
            reorderedOptions.splice(dragOverIndex, 0, removed);

            const updatedOptions = reorderedOptions.map((option, idx) => ({
                ...option,
                sort_order: idx + 1,
            }));

            onReorder(updatedOptions);
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [draggedIndex, dragOverIndex, sortOptions, onReorder]);

    if (sortOptions.length === 0) {
        return (
            <div
                id={`${COMPONENT_ID}-empty`}
                data-testid={`${COMPONENT_ID}-empty`}
                className="tw-text-center tw-py-8 tw-text-ink-secondary tw-text-body-primary"
            >
                No sort options available
            </div>
        );
    }

    return (
        <div
            id={COMPONENT_ID}
            data-testid={COMPONENT_ID}
            className="tw-border tw-border-solid tw-rounded-srf tw-border-edge tw-overflow-hidden"
        >
            <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                {/*
                    Structure kept on purpose. This is a DRAG-REORDERABLE list — rows
                    move with dnd — and DS `Table` has no reordering, so swapping it in
                    would trade working behaviour for consistency. Only the raw palette
                    (`zinc-200`, `gray-500`, `#DFE0E6`) moved onto the semantic tokens.
                */}
                <table className="tw-bg-surface-sunken tw-w-full tw-table-fixed">
                    <thead className="tw-sticky tw-top-0 tw-bg-surface-sunken">
                        <tr
                            id={`${COMPONENT_ID}-header`}
                            data-testid={`${COMPONENT_ID}-header`}
                        >
                            <th
                                id={`${COMPONENT_ID}-header-drag`}
                                data-testid={`${COMPONENT_ID}-header-drag`}
                                /* Fixed px, not a percentage — see the actions column below. */
                                className={`${headerClasses} tw-w-12`}
                                aria-label="Drag handle column"
                            />
                            <th
                                id={`${COMPONENT_ID}-header-title`}
                                data-testid={`${COMPONENT_ID}-header-title`}
                                className={`${headerClasses} tw-w-[32%]`}
                            >
                                <span>Sort Option Title</span>
                            </th>
                            <th
                                id={`${COMPONENT_ID}-header-type`}
                                data-testid={`${COMPONENT_ID}-header-type`}
                                className={`${headerClasses} tw-w-[12%]`}
                            >
                                <span>Type</span>
                            </th>
                            <th
                                id={`${COMPONENT_ID}-header-visible`}
                                data-testid={`${COMPONENT_ID}-header-visible`}
                                className={`${headerClasses} tw-w-[10%]`}
                            >
                                <span>Visible</span>
                            </th>
                            <th
                                id={`${COMPONENT_ID}-header-secondary-sort`}
                                data-testid={`${COMPONENT_ID}-header-secondary-sort`}
                                className={`${headerClasses} tw-w-[28%]`}
                            >
                                <span>Secondary Sort</span>
                            </th>

                            <th
                                id={`${COMPONENT_ID}-header-actions`}
                                data-testid={`${COMPONENT_ID}-header-actions`}
                                /*
                                   Fixed px, not a percentage. Under `table-fixed` a `5%`
                                   column is 5% of whatever the pane happens to be, while
                                   what it has to hold is a FIXED 72px: a 16px DeleteIcon
                                   in a `size="sm"` Button (`tw-px-3`, so ~40px) inside a
                                   cell with 32px of `tw-px-4`. 5% only clears that once the
                                   table is ~1440px wide; below it the button overflowed the
                                   cell, sat on the card's right border and pushed the table
                                   past its container — which is the horizontal scrollbar
                                   under this table. A control's width is not a proportion
                                   of the page. Same fix as mapping § 324.
                                */
                                className={`${headerClasses} tw-w-20`}
                                aria-label="Actions column"
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sortOptions.map((sortOption, index) => (
                            <SortOptionRow
                                key={`${sortOption.primary_sort_id}-${sortOption.sort_order}`}
                                sortOption={sortOption}
                                index={index}
                                onVisibilityChange={onVisibilityChange}
                                onSecondarySortChange={onSecondarySortChange}
                                onTitleChange={onTitleChange}
                                onDelete={onDelete}
                                secondarySortOptions={secondarySortOptions}
                                onDragStart={handleDragStart}
                                onDragOver={handleDragOver}
                                onDragEnd={handleDragEnd}
                                isDragging={draggedIndex === index}
                                dragOverIndex={dragOverIndex}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};